/**
 * Deej Gallery `/deej` — the public collection of every Deej artwork ever
 * minted, presented as collector cards. Data is the GENERATED
 * {@link MASCOT_ART} manifest (tools/mascot-gen — the ONE source for mascot
 * art): every scene ships card fields (title, rarity, crumbs, series, minted).
 * Card numbers are assigned by mint date (the reference sheet is #000).
 * Static page: no services, no fetches — sort/filter run client-side.
 * New art lands here automatically when a scene is added to the manifest and
 * regenerated (see skill djgg-deej-art).
 *
 * Deep links: the open card lives in the URL (`/deej/<scene-id>`), so every
 * card is shareable. The scene id is the ONLY deep-link key — the mint
 * `number` shifts when older art is backfilled. An unknown/stale id quietly
 * renders the plain gallery.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.js';
import { publicPortalOrigin } from '@djgg/cloud';
import { BADGES } from '../components/badgeManifest.js';
import { MASCOT_ART, type MascotArt, type MascotRarity } from '../components/mascotManifest.js';

const RARITY_ORDER: MascotRarity[] = ['Legendary', 'Epic', 'Rare', 'Uncommon', 'Common'];

const crumbsFormat = new Intl.NumberFormat('en-US');
const BADGE_CRITERIA = new Map(BADGES.map((badge) => [`badge-${badge.id}`, badge.criteria]));

type SortKey = 'newest' | 'oldest' | 'rarity' | 'crumbs' | 'title';

const SORTS: Record<SortKey, { label: string; compare: (a: Card, b: Card) => number }> = {
  newest: { label: 'Newest', compare: (a, b) => b.minted.localeCompare(a.minted) || a.number - b.number },
  oldest: { label: 'Oldest', compare: (a, b) => a.minted.localeCompare(b.minted) || a.number - b.number },
  rarity: {
    label: 'Rarity',
    compare: (a, b) => RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity) || b.crumbs - a.crumbs,
  },
  crumbs: { label: 'Value', compare: (a, b) => b.crumbs - a.crumbs },
  title: { label: 'Title', compare: (a, b) => a.title.localeCompare(b.title) },
};

interface Card extends MascotArt {
  /** Collection number: #000 is the reference sheet, then mint order. */
  number: number;
}

/** The full collection with stable card numbers (mint date, then id). */
function collection(): Card[] {
  const rest = MASCOT_ART.filter((a) => a.id !== 'card-origin').sort(
    (a, b) => a.minted.localeCompare(b.minted) || a.id.localeCompare(b.id),
  );
  const origin = MASCOT_ART.find((a) => a.id === 'card-origin');
  const cards: Card[] = rest.map((a, i) => ({ ...a, number: i + 1 }));
  return origin ? [{ ...origin, number: 0 }, ...cards] : cards;
}

const cardNo = (n: number) => `#${String(n).padStart(3, '0')}`;

/** '2026-07-29' → 'Jul 29' (the stat tile wants one line). */
function mintDay(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[(month ?? 1) - 1]} ${day ?? 1}`;
}

function RarityBadge({ rarity }: { rarity: MascotRarity }) {
  return <span className={`deej-rarity deej-rarity--${rarity.toLowerCase()}`}>{rarity}</span>;
}

function CardDetail({ card, onClose }: { card: Card; onClose: () => void }) {
  const badgeCriteria = BADGE_CRITERIA.get(card.id);
  const { copied, copy } = useCopyToClipboard();
  // Absolute so it survives being pasted anywhere. Production always shares
  // the branded host even if an old Firebase alias was opened from a bookmark.
  const shareUrl = `${publicPortalOrigin(window.location)}/deej/${card.id}`;
  const share = async () => {
    // The native share sheet where the platform has one (mobile); the
    // clipboard + "Copied!" acknowledgement everywhere else.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: card.title, url: shareUrl });
      } catch {
        // The user dismissed the sheet — not an error, nothing to acknowledge.
      }
      return;
    }
    await copy(shareUrl);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="deej-detail" role="dialog" aria-modal="true" aria-label={card.title} onClick={onClose}>
      <div className="deej-detail__card" onClick={(e) => e.stopPropagation()}>
        <img className="deej-detail__img" src={card.src} alt={card.alt} />
        <div className="deej-detail__body">
          <div className="deej-detail__head">
            <span className="deej-card__no">{cardNo(card.number)}</span>
            <RarityBadge rarity={card.rarity} />
          </div>
          <h2 className="deej-detail__title">{card.title}</h2>
          <p className="deej-detail__flavor">{card.quip ?? card.alt}</p>
          {badgeCriteria && (
            <p className="deej-detail__flavor" data-testid="deej-badge-criteria">
              <strong>Earned for:</strong> {badgeCriteria}
            </p>
          )}
          <dl className="deej-detail__facts">
            <div>
              <dt>Series</dt>
              <dd>{card.series}</dd>
            </div>
            <div>
              <dt>Minted</dt>
              <dd>{card.minted}</dd>
            </div>
            <div>
              <dt>Value</dt>
              <dd>{crumbsFormat.format(card.crumbs)} crumbs</dd>
            </div>
          </dl>
          <div className="deej-detail__actions">
            <button
              className="btn btn--ghost btn--sm"
              data-testid="deej-share"
              onClick={() => void share()}
            >
              {copied ? '✓ Copied!' : 'Share card'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DeejGallery() {
  const cards = useMemo(collection, []);
  const [sort, setSort] = useState<SortKey>('newest');
  const [rarity, setRarity] = useState<MascotRarity | 'all'>('all');
  const [series, setSeries] = useState<string>('all');
  const { cardId } = useParams();
  const navigate = useNavigate();
  // The URL is the ONE open-card state: `/deej/<id>` renders that card's
  // detail, `/deej` the plain grid — so browser Back closes the modal, and an
  // unknown id (typo, renamed art) just means no match ⇒ the plain gallery.
  const open = useMemo(() => (cardId ? (cards.find((c) => c.id === cardId) ?? null) : null), [cards, cardId]);
  const openCard = (card: Card) => navigate(`/deej/${card.id}`);
  // Replace, so pressing Back after an explicit close doesn't reopen the modal.
  const closeCard = () => navigate('/deej', { replace: true });

  const seriesNames = useMemo(() => [...new Set(cards.map((c) => c.series))].sort(), [cards]);
  const shown = useMemo(() => {
    const filtered = cards.filter(
      (c) => (rarity === 'all' || c.rarity === rarity) && (series === 'all' || c.series === series),
    );
    return [...filtered].sort(SORTS[sort].compare);
  }, [cards, sort, rarity, series]);

  const totalCrumbs = useMemo(() => cards.reduce((sum, c) => sum + c.crumbs, 0), [cards]);
  const newest = useMemo(() => cards.reduce((max, c) => (c.minted > max ? c.minted : max), ''), [cards]);
  const rarityCounts = useMemo(() => {
    const counts = new Map<MascotRarity, number>();
    for (const c of cards) counts.set(c.rarity, (counts.get(c.rarity) ?? 0) + 1);
    return counts;
  }, [cards]);

  return (
    <div className="container deej" data-testid="deej-gallery">
      <div className="gallery__hero">
        <h1 className="gallery__title">The Deej Gallery</h1>
        <p className="gallery__subtitle">
          Every portrait of Deej — resident pigeon, professional beta tester — and his whole flock,
          minted as collector cards. The collection only ever grows.
        </p>
      </div>

      <div className="deej-stats" data-testid="deej-stats">
        <div className="stat-tile">
          <div className="stat-tile__value">{cards.length}</div>
          <div className="stat-tile__label">cards minted</div>
        </div>
        <div className="stat-tile">
          <div className="stat-tile__value">{crumbsFormat.format(totalCrumbs)}</div>
          <div className="stat-tile__label">crumbs of value</div>
          <div className="stat-tile__sub">appraised, not for sale</div>
        </div>
        <div className="stat-tile">
          <div className="stat-tile__value">{rarityCounts.get('Legendary') ?? 0}</div>
          <div className="stat-tile__label">legendaries</div>
        </div>
        <div className="stat-tile">
          <div className="stat-tile__value">{mintDay(newest)}</div>
          <div className="stat-tile__label">latest mint</div>
        </div>
      </div>

      <div className="toolbar deej-toolbar">
        <label className="deej-toolbar__field">
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} data-testid="deej-sort">
            {(Object.keys(SORTS) as SortKey[]).map((key) => (
              <option key={key} value={key}>
                {SORTS[key].label}
              </option>
            ))}
          </select>
        </label>
        <label className="deej-toolbar__field">
          Series
          <select value={series} onChange={(e) => setSeries(e.target.value)} data-testid="deej-series">
            <option value="all">All series</option>
            {seriesNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <div className="deej-toolbar__rarities" role="group" aria-label="Filter by rarity">
          <button
            className={`deej-chip ${rarity === 'all' ? 'deej-chip--on' : ''}`}
            onClick={() => setRarity('all')}
          >
            All
          </button>
          {RARITY_ORDER.map((tier) => (
            <button
              key={tier}
              className={`deej-chip deej-chip--${tier.toLowerCase()} ${rarity === tier ? 'deej-chip--on' : ''}`}
              onClick={() => setRarity(rarity === tier ? 'all' : tier)}
            >
              {tier} · {rarityCounts.get(tier) ?? 0}
            </button>
          ))}
        </div>
      </div>

      <div className="deej-grid" data-testid="deej-grid">
        {shown.map((card) => (
          <button
            key={card.id}
            className={`deej-card deej-card--${card.rarity.toLowerCase()}`}
            onClick={() => openCard(card)}
            data-testid="deej-card"
            data-card-id={card.id}
          >
            <div className="deej-card__frame">
              <img className="deej-card__img" src={card.src} alt={card.alt} loading="lazy" />
            </div>
            <div className="deej-card__body">
              <div className="deej-card__row">
                <span className="deej-card__no">{cardNo(card.number)}</span>
                <RarityBadge rarity={card.rarity} />
              </div>
              <div className="deej-card__title">{card.title}</div>
              <div className="deej-card__meta">
                <span>{card.series}</span>
                <span className="deej-card__crumbs">{crumbsFormat.format(card.crumbs)} crumbs</span>
              </div>
            </div>
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="deej-empty">No cards match that filter. Deej is offended.</p>}

      {open && <CardDetail card={open} onClose={closeCard} />}
    </div>
  );
}
